import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { IconButton, Menu, MenuItem, Pagination, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ReportContext } from '../../store/reportContext';
import ReportOverview from './report/Overview';

type Props = {
    storage: StorageType;
};

const ROWS_PER_PAGE = 5;

const StockedTakingReports: React.FC<Props> = ({ storage }) => {
    const theme = useTheme();
    const { getReports, getReportsLoading, deleteReport } = useContext(ReportContext);
    const [reports, setReports] = useState<VptReport[]>([]);
    const [page, setPage] = useState(1);
    const [selectedReport, setSelectedReport] = useState<VptReport | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [actionReport, setActionReport] = useState<VptReport | null>(null);

    useEffect(() => {
        getReports(false, {
            storageId: storage.id,
            inventoryReports: false,
        }, (results) => {
            setReports(results.reportHeaders || []);
        });
    }, [getReports, storage.id]);

    const rows = useMemo(() => {
        return reports.map((report) => {
            const stocked = report.stockingLogs?.reduce((sum, log) => sum + (log.amountAdded || 0), 0) || 0;
            const missing = report.stockingLogs?.reduce((sum, log) => sum + (log.amountMissing || 0), 0) || 0;

            return {
                id: report.id,
                reporterName: report.reporterName,
                date: report.date,
                stocked,
                missing,
                report,
            };
        });
    }, [reports]);

    const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
    const paginatedRows = useMemo(() => {
        const start = (page - 1) * ROWS_PER_PAGE;
        return rows.slice(start, start + ROWS_PER_PAGE);
    }, [page, rows]);

    useEffect(() => {
        setPage(1);
        setSelectedReport(null);
    }, [storage.id]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, report: VptReport) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setActionReport(report);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadReport = () => {
        if (actionReport) {
            setSelectedReport(actionReport);
        }
        handleMenuClose();
    };

    const handleDeleteReport = () => {
        if (!actionReport) {
            return;
        }

        const reportToDelete = actionReport;
        handleMenuClose();

        deleteReport(reportToDelete.id, () => {
            getReports(true, {
                storageId: storage.id,
                inventoryReports: false,
            }, (results) => {
                setReports(results.reportHeaders || []);
            });

            if (selectedReport?.id === reportToDelete.id) {
                setSelectedReport(null);
            }
        });
    };

    const dateToString = (date: Date) => {
        if (!date) {
            return '-';
        }

        const parsedDate = new Date(date);
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        const year = parsedDate.getFullYear();
        const hours = String(parsedDate.getHours()).padStart(2, '0');
        const minutes = String(parsedDate.getMinutes()).padStart(2, '0');

        return `${month}.${day}.${year}, ${hours}:${minutes}`;
    };

    return (
        <Grid container spacing={2} size={{ xs: 12 }} sx={{ p: 2 }}>
            <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
                    Stocktaking reports
                </Typography>

                <Grid container size={{ xs: 12 }} sx={{ mt: 1, border: theme.palette.border }}>
                    <Grid size={{ xs: 4 }} sx={{ p: 1, borderRight: theme.palette.border }}>
                        <Typography variant="caption" sx={{ ...theme.typography.columnHeader }}>
                            Reporter
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }} sx={{ p: 1, borderRight: theme.palette.border }}>
                        <Typography variant="caption" sx={{ ...theme.typography.columnHeader }}>
                            Date and time
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }} sx={{ p: 1, borderRight: theme.palette.border }}>
                        <Typography variant="caption" sx={{ ...theme.typography.columnHeader }}>
                            Stocked
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }} sx={{ p: 1 }}>
                        <Typography variant="caption" sx={{ ...theme.typography.columnHeader }}>
                            Missing
                        </Typography>
                    </Grid>

                    {getReportsLoading && (
                        <Grid size={{ xs: 12 }} sx={{ p: 1 }}>
                            <Typography variant="body2">Loading...</Typography>
                        </Grid>
                    )}

                    {!getReportsLoading && rows.length === 0 && (
                        <Grid size={{ xs: 12 }} sx={{ p: 1 }}>
                            <Typography variant="body2">No reports</Typography>
                        </Grid>
                    )}

                    {!getReportsLoading && paginatedRows.map((row) => (
                        <Grid
                            key={row.id}
                            container
                            size={{ xs: 12 }}
                            sx={{
                                '& .report-row-actions': {
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    transition: 'opacity 150ms ease-in-out',
                                },
                                '&:hover .report-row-cell': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                                '&:hover .report-row-actions': {
                                    opacity: 1,
                                    pointerEvents: 'auto',
                                },
                            }}
                        >
                            <Grid className="report-row-cell" size={{ xs: 4 }} sx={{ p: 1, borderTop: theme.palette.border, borderRight: theme.palette.border }}>
                                <Typography
                                    component="button"
                                    type="button"
                                    onClick={() => setSelectedReport(row.report)}
                                    sx={{
                                        ...theme.typography.usersFontRegular,
                                        p: 0,
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        color: theme.palette.primary.main,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {row.reporterName}
                                </Typography>
                            </Grid>
                            <Grid className="report-row-cell" size={{ xs: 4 }} sx={{ p: 1, borderTop: theme.palette.border, borderRight: theme.palette.border }}>
                                <Typography component="button"
                                    type="button"
                                    onClick={() => setSelectedReport(row.report)}
                                    sx={{
                                        ...theme.typography.usersFontRegular,
                                        p: 0,
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        color: theme.palette.primary.main,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                    >
                                        {dateToString(row.date)}</Typography>
                            </Grid>
                            <Grid className="report-row-cell" size={{ xs: 2 }} sx={{ p: 1, borderTop: theme.palette.border, borderRight: theme.palette.border }}>
                                <Typography variant="body2">{row.stocked}</Typography>
                            </Grid>
                            <Grid className="report-row-cell" size={{ xs: 2 }} sx={{ p: 1, borderTop: theme.palette.border }}>
                                <Grid container wrap="nowrap" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                                    <Typography variant="body2">{row.missing}</Typography>
                                    <Grid className="report-row-actions" container wrap="nowrap" alignItems="center" sx={{ ml: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, row.report)}
                                            sx={{ color: 'green' }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuItem onClick={handleDownloadReport}>
                        <DownloadIcon fontSize="small" sx={{ mr: 1, color: 'green' }} />
                        Download
                    </MenuItem>
                    <MenuItem onClick={handleDeleteReport}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'green' }} />
                        Delete
                    </MenuItem>
                </Menu>

                {!getReportsLoading && rows.length > ROWS_PER_PAGE && (
                    <Grid container size={{ xs: 12 }} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                        <Pagination
                            page={page}
                            count={pageCount}
                            onChange={(_, value) => setPage(value)}
                            size="small"
                            shape="rounded"
                        />
                    </Grid>
                )}

                <ReportOverview
                    report={selectedReport}
                    open={Boolean(selectedReport)}
                    onClose={() => setSelectedReport(null)}
                />
            </Grid>
        </Grid>
    );
};

export default StockedTakingReports;
